import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import ProviderCard from "@/components/ProviderCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Stars } from "@/components/Badges";
import { Search, Star, Repeat, Sparkles, DollarSign, ShieldCheck, ArrowRight, Leaf, Scissors, Trees, TreeDeciduous, CheckCircle2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1734303023491-db8037a21f09?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const TESTI = "https://images.pexels.com/photos/7728908/pexels-photo-7728908.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  const navigate = useNavigate();
  const [zip, setZip] = useState("");
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);
  const [season, setSeason] = useState(null);
  const [seasonal, setSeasonal] = useState([]);

  useEffect(() => {
    api.get("/providers/search?sort=recommended").then((r) => setFeatured(r.data.providers.slice(0, 4))).catch(() => {});
    api.get("/config").then((r) => setCats(r.data.service_categories.slice(0, 8))).catch(() => {});
    api.get("/season").then((r) => setSeason(r.data)).catch(() => {});
    api.get("/providers/seasonal").then((r) => setSeasonal(r.data.providers.slice(0, 4))).catch(() => {});
  }, []);

  const search = () => navigate(`/search?zip=${zip}`);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative hero-gradient grain text-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 fade-up">
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide">
              <Sparkles className="w-3.5 h-3.5 text-gold" /> {season ? `${season.name} season · Lawn care all year round` : "#1 Local Lawn Care Marketplace"}
            </span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl font-extrabold leading-[1.02]">
              Book trusted lawn care in <span className="text-lawn">minutes.</span>
            </h1>
            <p className="mt-5 text-lg text-white/70 max-w-lg leading-relaxed">
              Compare local landscapers by price, ratings, and availability. One-time mowing, recurring maintenance, seasonal cleanup, or full landscaping projects.
            </p>
            <div className="mt-8 bg-white rounded-2xl p-2 flex items-center gap-2 max-w-md shadow-2xl">
              <div className="flex items-center gap-2 flex-1 pl-3">
                <Search className="w-5 h-5 text-muted-foreground" />
                <Input data-testid="hero-zip-input" value={zip} onChange={(e) => setZip(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="Enter your ZIP code" className="border-0 shadow-none focus-visible:ring-0 text-lawn-ink px-0" />
              </div>
              <Button data-testid="hero-search-btn" onClick={search} className="rounded-xl bg-lawn hover:bg-lawn-dark font-semibold px-6">Find Pros</Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button data-testid="cta-book-btn" onClick={() => navigate("/search")} className="rounded-full bg-gold hover:bg-gold-dark text-white font-semibold gap-2">Book Lawn Service <ArrowRight className="w-4 h-4" /></Button>
              <Button data-testid="cta-list-btn" onClick={() => navigate("/signup?role=provider")} variant="outline" className="rounded-full bg-white/5 border-white/20 text-white hover:bg-white/10 font-semibold">List Your Lawn Business</Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-white/60">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-lawn" /> Verified pros</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-lawn" /> Secure payments</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-lawn" /> Free to browse</span>
            </div>
          </div>
          <div className="lg:col-span-6 relative fade-up" style={{ animationDelay: "120ms" }}>
            <div className="rounded-[2rem] overflow-hidden border-4 border-white/10 shadow-2xl">
              <img src={season?.hero || HERO} alt={season ? `${season.name} lawn care` : "Professional mowing a lawn"} className="w-full h-[440px] object-cover transition-all duration-700" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 shadow-xl hidden sm:block">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {[1,2,3].map(i => <div key={i} className="w-8 h-8 rounded-full bg-lawn border-2 border-white" />)}
                </div>
                <div>
                  <Stars rating={4.9} />
                  <p className="text-xs text-muted-foreground">3,200+ happy homeowners</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-lawn">Services</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-lawn-ink mt-2">What do you need done?</h2>
          </div>
          <Link to="/search" className="text-sm font-semibold text-lawn hover:underline hidden sm:block">Browse all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cats.map((c, i) => (
            <button key={c.key} data-testid={`category-${c.key}`} onClick={() => navigate(`/search?service=${c.key}`)}
              className="card-hover bg-white border border-border rounded-2xl p-6 text-left group fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="w-11 h-11 rounded-xl bg-lawn-light flex items-center justify-center mb-4 group-hover:bg-lawn transition-colors">
                <Leaf className="w-5 h-5 text-lawn-dark group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-display font-bold text-lawn-ink">{c.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">from ${c.base_price}</p>
            </button>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-border py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-lawn-ink text-center">How LawnLink works</h2>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              { icon: Search, t: "Search & Compare", d: "Enter your ZIP and compare local pros by price, rating, and availability side by side." },
              { icon: CheckCircle2, t: "Book or Request Quote", d: "Book instantly, schedule recurring maintenance, or request a custom quote in seconds." },
              { icon: Star, t: "Relax & Review", d: "Track your service, pay securely, and leave a review to help your neighbors." },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-lawn mx-auto flex items-center justify-center mb-4"><s.icon className="w-7 h-7 text-white" /></div>
                <h3 className="font-display font-bold text-xl text-lawn-ink">{s.t}</h3>
                <p className="text-muted-foreground mt-2 max-w-xs mx-auto">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seasonal spotlight */}
      {season && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16">
          <div className="rounded-2xl border border-border bg-white p-6 sm:p-8" style={{ borderTopWidth: 4, borderTopColor: season.accent }}>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: season.accent }}>{season.name} Season · Year-Round Care</p>
                <h2 className="font-display text-3xl font-extrabold text-lawn-ink mt-2">{season.tagline}</h2>
                <p className="text-muted-foreground mt-2 max-w-xl">{season.note} Lawn care and landscaping happen every season — here's what's in demand right now.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {season.highlight_services?.map((s) => (
                  <button key={s.key} data-testid={`seasonal-service-${s.key}`} onClick={() => navigate(`/search?service=${s.key}`)}
                    className="px-4 py-2 rounded-full text-sm font-semibold text-white transition-transform hover:scale-105" style={{ backgroundColor: season.accent }}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Top in-season providers */}
      {seasonal.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-12">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold">In-Season Pros</p>
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-lawn-ink mt-2">Top {season?.name} providers near you</h2>
              <p className="text-muted-foreground mt-1">Providers offering {season?.name.toLowerCase()} services right now.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {seasonal.map((p, i) => <ProviderCard key={p.provider_id} provider={p} index={i} />)}
          </div>
        </section>
      )}

      {/* Featured providers */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-lawn">Top Rated</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-lawn-ink mt-2">Featured providers</h2>
          </div>
          <Button data-testid="compare-cta-btn" onClick={() => navigate("/compare")} variant="outline" className="rounded-full font-semibold">Compare Providers</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p, i) => <ProviderCard key={p.provider_id} provider={p} index={i} />)}
        </div>
      </section>

      {/* Provider CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="rounded-[2rem] hero-gradient grain relative overflow-hidden p-10 sm:p-16 grid lg:grid-cols-2 gap-10 items-center text-white">
          <div className="relative">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold">For Landscapers</p>
            <h2 className="font-display text-4xl font-extrabold mt-3 leading-tight">Grow your lawn business on autopilot.</h2>
            <p className="mt-4 text-white/70 max-w-md">Get more customers, premium placement, and AI-powered coaching. Choose a monthly plan or pay only a commission on completed jobs.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button data-testid="provider-signup-cta" onClick={() => navigate("/signup?role=provider")} className="rounded-full bg-lawn hover:bg-lawn-dark font-semibold gap-2">List Your Business <ArrowRight className="w-4 h-4" /></Button>
              <Button data-testid="provider-pricing-cta" onClick={() => navigate("/pricing")} variant="outline" className="rounded-full bg-white/5 border-white/20 text-white hover:bg-white/10 font-semibold">See Pricing</Button>
            </div>
          </div>
          <div className="relative grid grid-cols-2 gap-4">
            {[
              { icon: DollarSign, t: "Earn More", d: "Higher visibility = more bookings" },
              { icon: Sparkles, t: "Premium Placement", d: "Featured & sponsored spots" },
              { icon: Repeat, t: "Recurring Revenue", d: "Weekly & monthly plans" },
              { icon: ShieldCheck, t: "Get Verified", d: "Build trust with badges" },
            ].map((f, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <f.icon className="w-6 h-6 text-gold mb-2" />
                <h4 className="font-display font-bold">{f.t}</h4>
                <p className="text-xs text-white/60 mt-1">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial + Affiliate */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-8">
        <div className="rounded-2xl overflow-hidden relative">
          <img src={TESTI} alt="Happy customers in their garden" className="w-full h-full object-cover min-h-[280px]" />
          <div className="absolute inset-0 bg-gradient-to-t from-lawn-ink/90 to-transparent flex items-end p-8">
            <div className="text-white">
              <Stars rating={5} className="mb-3" />
              <p className="font-display text-xl font-bold max-w-sm">"Booked a recurring mow in 2 minutes. Our yard has never looked better."</p>
              <p className="text-white/60 text-sm mt-2">— The Hendersons, Austin TX</p>
            </div>
          </div>
        </div>
        <div className="bg-gold-light rounded-2xl p-10 flex flex-col justify-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-dark">Affiliate Program</p>
          <h2 className="font-display text-3xl font-extrabold text-lawn-ink mt-3">Refer friends. Earn rewards.</h2>
          <p className="text-lawn-ink/70 mt-3">Share your referral link and earn credits when friends book or providers join. Plus exclusive partner offers on equipment, products, and more.</p>
          <div className="mt-6">
            <Button data-testid="affiliate-cta-btn" onClick={() => navigate("/affiliate")} className="rounded-full bg-gold hover:bg-gold-dark text-white font-semibold gap-2">Join the Affiliate Program <ArrowRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
